import { createFileRoute } from "@tanstack/react-router";
import { Sparkles, Activity, ArrowUpRight, Building2 } from "lucide-react";
import { Shell } from "@/components/control-tower/Layout";
import { InventoryMap } from "@/components/control-tower/InventoryMap";
import { BuildingList } from "@/components/control-tower/BuildingList";
import { useRole } from "@/lib/roles";
import { roleGreeting } from "@/lib/role-content";
import { getSession } from "@/lib/auth";
import { KairosLogo } from "@/components/control-tower/KairosLogo";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { ScopedLink } from "@/lib/scoped-nav";
import energyImg from "@/assets/energy-consumption.jpg";
import { moduleCard, cardBase } from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { SectionPanel } from "@/components/ui/section-panel";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/")({
  component: Index,
});

function firstNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  const first = local.split(/[._-]/)[0] ?? local;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function Index() {
  const { role, config } = useRole();
  const rawGreeting = roleGreeting[role];
  const firstName = firstNameFromEmail(getSession()?.email ?? "");
  const greeting = {
    ...rawGreeting,
    headline: rawGreeting.headline.replace("Salla", firstName),
  };
  const { filter, isFiltered, countLabel, activeGroup } = useInventoryFilter();

  return (
    <Shell
      title="Kiinteistökanta"
      subtitle={`Tilannekuva ${countLabel} · näkymä: ${config.label}`}
    >
      <div
        className={cn(
          cardBase,
          "mb-4 relative overflow-hidden rounded-2xl shadow-elevated",
        )}
      >
        <img
          src={energyImg}
          alt="Kiinteistöjen energiankulutuksen visualisointi"
          width={1024}
          height={512}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/75 to-background/30" />
        <div className="relative p-6 sm:p-8 flex items-start gap-4">
          <KairosLogo size={56} className="mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
              <span>BKRY</span>
              <span className="opacity-60">/</span>
              <span>Kairos · Digitaalinen kiinteistöjohtaja</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
              {greeting.headline}
            </h1>
            <p className="text-sm text-foreground mt-1.5 max-w-2xl">
              {greeting.bodyParts.before}
              <span className="font-semibold text-primary">
                {greeting.bodyParts.highlight}
              </span>
              {greeting.bodyParts.after}
            </p>
            {isFiltered && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[11px] text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                Rajaus aktiivinen
                {activeGroup && <span>· {activeGroup.name}</span>}
                {filter.city !== "all" && <span>· {filter.city}</span>}
                {filter.type !== "all" && <span>· {filter.type}</span>}
                {filter.status !== "all" && (
                  <span>
                    ·{" "}
                    {filter.status === "ok"
                      ? "Optimoidut"
                      : filter.status === "warn"
                        ? "Potentiaalia"
                        : "Toimi nyt"}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <SectionPanel icon={Building2} title="Kiinteistöt" className="mt-6">
        <BuildingList />
      </SectionPanel>

      <div className="mt-6">
        <InventoryMap />
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NavCard
          to="/recommendations"
          icon={Sparkles}
          useLogo
          title="Suositukset"
          desc="AI-ehdotukset uusimmasta vanhimpaan — hyväksy tai hylkää yhdellä painikkeella."
          cta="Avaa suositukset"
        />
        <NavCard
          to="/activity"
          icon={Activity}
          title="Tapahtumaloki"
          desc="Toteutunut toiminta — mitä Kairos ja tiimi teki kiinteistöillesi."
          cta="Avaa tapahtumaloki"
        />
      </div>
    </Shell>
  );
}

function NavCard({
  to,
  icon: Icon,
  title,
  desc,
  cta,
  useLogo = false,
}: {
  to: "/recommendations" | "/activity";
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  cta: string;
  useLogo?: boolean;
}) {
  return (
    <ScopedLink to={to} className={`${moduleCard} p-4 flex items-start gap-3`}>
      <CardGlowOverlay />
      {useLogo ? (
        <KairosLogo size={36} className="relative shrink-0" />
      ) : (
        <div className="relative h-9 w-9 rounded-md bg-gradient-accent grid place-items-center shrink-0 shadow-glow">
          <Icon className="h-4 w-4 text-primary-foreground" />
        </div>
      )}
      <div className="relative flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
          <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
        <p className="text-xs text-muted-foreground mt-1">{desc}</p>
        <span className="inline-block mt-2 text-[11px] font-medium text-primary">
          {cta} →
        </span>
      </div>
    </ScopedLink>
  );
}
